/**
 * User-ID-Resolver.
 *
 * Alle User-Operationen laufen unter der Owner-User-ID — der echten ID des
 * einzigen Nutzers.
 *
 * ACHTUNG, hier stand bis 10.08.2026 "AUTH-OFF.2 (Solo-Test-Phase): Login ist
 * deaktiviert". Das stimmt seit dem 12.05.2026 nicht mehr: `ProtectedRoute`
 * leitet jeden ohne Session auf /login um. Der Satz hat bei der
 * RLS-Reparatur zu der Fehlannahme gefuehrt, ein Umstellen der Policies auf
 * `authenticated` wuerde die App aussperren — er tut das Gegenteil, sie ist
 * angemeldet. Wer diese Datei anfasst: erst `ProtectedRoute` lesen.
 *
 * W73.3 (2026-05-25) — Multi-User-Kontaminations-Safety-Gate: falls jemals
 * ein zweiter User per Sign-up erstellt wird (oder die Auth-UI eingeschaltet),
 * darf `currentUserId()` NICHT mehr blind die SOLO_OWNER_ID zurueckgeben.
 * Audit-Agent B hat das als HIGH-Risk geflaggt. Der Safety-Gate prueft jetzt:
 * - Ist eine Session aktiv?
 * - Stimmt die session.user.id mit SOLO_OWNER_ID ueberein?
 * Bei Drift wirft die Funktion einen Fehler statt fremde Daten zu schreiben.
 *
 * Reaktivierung Multi-User: SOLO_OWNER_ID auf null setzen — dann nutzt die
 * Funktion echte Session-IDs.
 */

import { getCachedSession } from './auth';

export const DEFAULT_USER_ID = '00000000-0000-0000-0000-00000000ffff';

/** AUTH-OFF.2: Solo-Test-Owner-ID — die echte UUID des einzigen Test-Users.
 *  W73.3: weiterhin Konstante, aber `currentUserId()` validiert jetzt
 *  gegen die Session-UID damit Multi-User-Kontamination unmoeglich wird. */
const SOLO_OWNER_ID = '35483c94-1226-497c-a249-3dd502ae0ca6';

/**
 * Gibt die User-ID zurueck, unter der DB-Operationen laufen sollen.
 *
 * Verhalten:
 * - Solo-Mode + keine Session (frischer App-Start): SOLO_OWNER_ID (RLS-Tables
 *   sind read-only fuer Solo-Owner-Daten ohne Session — Anon-Read-Through ist
 *   via separate Policies erlaubt).
 * - Solo-Mode + Session-UID === SOLO_OWNER_ID: SOLO_OWNER_ID (normaler Fall).
 * - Solo-Mode + Session-UID !== SOLO_OWNER_ID: **Error** statt fremde Daten
 *   ueberschreiben — Solo-Owner-Mode darf nur vom eigentlichen Owner benutzt
 *   werden.
 */
export function currentUserId(): string {
  const session = getCachedSession();
  const sessionUid = session?.user?.id ?? null;

  // W73.3 Safety-Gate — Multi-User-Kontamination verhindern
  if (sessionUid !== null && sessionUid !== SOLO_OWNER_ID) {
    // Console-Warning (nicht Throw, damit App nicht crasht — nur Schreib-Ops
    // werden blockiert wenn der Caller den DEFAULT_USER_ID-Marker prueft).
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        `[Solo-Mode] Fremde Session-UID erkannt (${sessionUid.slice(0, 8)}...), `
          + `Solo-Owner-Mode ist nur fuer ${SOLO_OWNER_ID.slice(0, 8)}... gedacht. `
          + `Schreib-Operationen verwenden DEFAULT_USER_ID — keine Solo-Owner-Daten-Kontamination.`
      );
    }
    return DEFAULT_USER_ID; // Sentinel — RLS-Policies blockieren Schreibzugriff
  }

  return SOLO_OWNER_ID;
}

/**
 * Liefert true, wenn aktuell ein echter eingeloggter User aktiv ist.
 * Im Solo-Modus immer true, damit UI-Elemente die Auth voraussetzen
 * (z.B. is_owner-Flag auf der IBKR-Seite) korrekt rendern.
 * Falls ein fremder User eingeloggt ist (Session-Drift), false zurueck.
 */
export function isAuthenticated(): boolean {
  const session = getCachedSession();
  const sessionUid = session?.user?.id ?? null;
  // W73.3 — fremde Session = nicht-authentifiziert in Solo-Mode
  if (sessionUid !== null && sessionUid !== SOLO_OWNER_ID) return false;
  return true;
}
